import { Source, Layer } from 'react-map-gl/mapbox';
import type { LineLayerSpecification } from 'mapbox-gl';
import { useGcsStore } from '../../../store/useGcsStore';
import { selectIsRouteToSafePointLocked } from '../../../store/selectors/selectIsRouteToSafePointLocked';
import { getMapElementZIndex, MapElement } from '../../../types/ui/mapElement';
import { SafePointMarker } from './markers/SafePointMarker';
import type { MissionId } from '../../../types/gcs/mission';

interface SingleMissionFailureModeRouteMapElementsProps {
  gcsId: string;
  missionId: MissionId;
}

const createRouteToSafePointLineLayer = (
  isLocked: boolean,
  layerId: string,
  sourceId: string
): LineLayerSpecification => {
  const paint = isLocked
    ? {
        'line-color': '#ec221f',
        'line-width': 4,
        'line-opacity': 1
      }
    : {
        'line-color': '#ffffff',
        'line-width': 4,
        'line-opacity': 0.5
      };

  return {
    id: layerId,
    type: 'line',
    source: sourceId,
    paint
  };
};

export const SingleMissionFailureModeRouteMapElements = ({
  gcsId,
  missionId
}: SingleMissionFailureModeRouteMapElementsProps) => {
  const failureModeRouteInfo = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    return gcsData?.missionFailureModeRouteInfoMap?.get(missionId);
  });

  const isRouteToSafePointLocked = useGcsStore((state) => selectIsRouteToSafePointLocked(state.data, gcsId, missionId));

  if (!failureModeRouteInfo) {
    return null;
  }

  const coordinates = failureModeRouteInfo.routeToSafePoint.map((location) => [location.lng, location.lat]);

  if (coordinates.length === 0 && failureModeRouteInfo.safePoints.length === 0) {
    return null;
  }

  const sourceId = `route-to-safe-point-${gcsId}-${missionId}`;
  const layerId = `route-to-safe-point-line-z${getMapElementZIndex(MapElement.ROUTE_TO_SAFE_POINT)}-${gcsId}-${missionId}`;
  const lockedSegmentAnchorLayerId = `locked-segment-line-${gcsId}-${missionId}-0`;

  const routeLineLayer = createRouteToSafePointLineLayer(isRouteToSafePointLocked, layerId, sourceId);

  const routeGeoJSON = {
    type: 'Feature' as const,
    geometry: {
      type: 'LineString' as const,
      coordinates
    },
    properties: {
      missionId
    }
  };

  return (
    <>
      {/* Route to safe point polyline */}
      {coordinates.length > 0 && (
        <Source id={sourceId} type='geojson' data={routeGeoJSON}>
          <Layer {...routeLineLayer} beforeId={lockedSegmentAnchorLayerId} />
        </Source>
      )}

      {/* Safe point markers */}
      {failureModeRouteInfo.safePoints.map((safePoint, index) => (
        <SafePointMarker key={`safe-point-${gcsId}-${missionId}-${index}`} safePoint={safePoint} />
      ))}
    </>
  );
};
